import React from 'react';
import { Zap, ChevronDown, Music, Mic2, Sparkles } from 'lucide-react';
import { useMode } from '../ModeContext';
import { useStudioForm } from '@context/StudioFormContext';
import { useVocalPacks } from '@hooks/useVocalPacks';
import { useStylePresets } from '@hooks/useStylePresets';
import { HookhouseFormData } from '@/types';
import { useVocalConfig } from '@context/VocalConfigContext';

interface QuickModeStudioProps {
  onGenerate: (data: HookhouseFormData, vocalArch?: string) => void;
  isGenerating: boolean;
}

export function QuickModeStudio({ onGenerate, isGenerating }: QuickModeStudioProps) {
  const { data: VOCAL_PACKS } = useVocalPacks();
  const { allPresets: STYLE_PRESETS } = useStylePresets();
  const { getActiveVocalArchitecture, setSinglePack } = useVocalConfig();
  const { formData, updateField } = useStudioForm();

  const canGenerate = formData.seed.trim().length > 0;

  const handleGenerate = () => {
    const currentData: HookhouseFormData = {
      ...formData,
      vocalPackKey: formData.stylePreset !== 'None' ? 'None' : formData.vocalPackKey,
    };

    if (formData.stylePreset === 'None' && formData.vocalPackKey !== 'None') {
      setSinglePack(formData.vocalPackKey);
    }

    const vocalArch = formData.stylePreset !== 'None' ? '' : getActiveVocalArchitecture();
    onGenerate(currentData, vocalArch);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 border border-accent/30 rounded-full">
          <Zap size={14} className="text-accent" fill="currentColor" />
          <span className="text-[11px] font-black uppercase tracking-[0.2em] text-accent">Quick Mode Active</span>
        </div>
        <h2 className="text-4xl font-black text-slate-50 uppercase tracking-tighter">What's the next anthem?</h2>
        <p className="text-slate-400 text-sm max-w-md mx-auto leading-relaxed font-medium italic">Enter an idea and Big Chuck will handle the production architecture.</p>
      </div>

      <div className="space-y-6">
        <div className="bg-slate-900/60 backdrop-blur-md p-8 rounded-[32px] border-l-4 border-l-accent shadow-2xl space-y-8">
          {/* Seed Input */}
          <div className="space-y-3">
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Music size={14} className="text-accent" /> Song Title or Seed Idea
            </label>
            <input
              type="text"
              value={formData.seed}
              onChange={(e) => updateField('seed', e.target.value)}
              placeholder="e.g. Neon ghosts on the interstate..."
              className="w-full bg-slate-950 border-2 border-slate-800 rounded-2xl p-5 text-lg text-slate-100 placeholder-slate-700 focus:border-accent outline-none transition-all shadow-inner"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Style Preset */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Sparkles size={14} className="text-accent" /> Style Blueprint
              </label>
              <div className="relative">
                <select
                  value={formData.stylePreset}
                  onChange={(e) => {
                    updateField('stylePreset', e.target.value);
                    if (e.target.value !== 'None') updateField('vocalPackKey', 'None');
                  }}
                  className="w-full bg-slate-950 border-2 border-slate-800 rounded-2xl p-4 text-sm text-accent font-black uppercase outline-none appearance-none cursor-pointer hover:border-slate-700 transition-colors"
                >
                  <option value="None">MANUAL CONFIGURATION</option>
                  {Object.keys(STYLE_PRESETS).map(name => (
                    <option key={name} value={name}>{name.toUpperCase()}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>

            {/* Vocal Selector */}
            <div className={`space-y-3 transition-all ${formData.stylePreset !== 'None' ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Mic2 size={14} className="text-accent" /> Vocal Identity
              </label>
              <div className="relative">
                <select
                  value={formData.vocalPackKey}
                  onChange={(e) => {
                    updateField('vocalPackKey', e.target.value);
                    setSinglePack(e.target.value);
                  }}
                  className="w-full bg-slate-950 border-2 border-slate-800 rounded-2xl p-4 text-sm text-slate-200 font-bold uppercase outline-none appearance-none cursor-pointer hover:border-slate-700 transition-colors"
                >
                  <option value="None">AUTO (GENRE BASED)</option>
                  {Object.keys(VOCAL_PACKS).map(key => (
                    <option key={key} value={key}>{VOCAL_PACKS[key].name.replace('VocalForge: ', '').toUpperCase()}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!canGenerate || isGenerating}
            className={`
              w-full py-6 rounded-[20px] font-black text-xl uppercase tracking-tighter transition-all duration-300
              flex items-center justify-center gap-4 shadow-2xl
              ${canGenerate && !isGenerating
                ? 'bg-accent hover:bg-amber-400 text-slate-950 shadow-amber-900/20 active:scale-[0.98]'
                : 'bg-slate-800 text-slate-400 cursor-not-allowed opacity-50'
              }
            `}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin h-6 w-6 border-4 border-slate-950 border-t-transparent rounded-full" />
                <span>Architecting...</span>
              </>
            ) : (
              <>
                <Zap size={24} fill="currentColor" />
                <span>Synthesize Song</span>
              </>
            )}
          </button>
        </div>
        
        <p className="text-center text-[11px] text-slate-400 font-black uppercase tracking-[0.3em]">
          Powered by Session Architect v2.6.1 • Southern Rock Realism Engine
        </p>
      </div>
    </div>
  );
}